package one.healthium.ui.compose.components

import androidx.compose.animation.core.Animatable
import androidx.compose.animation.core.exponentialDecay
import androidx.compose.animation.core.tween
import androidx.compose.foundation.Canvas
import androidx.compose.foundation.gestures.awaitEachGesture
import androidx.compose.foundation.gestures.awaitFirstDown
import androidx.compose.foundation.gestures.drag
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.Stable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.runtime.rememberUpdatedState
import androidx.compose.runtime.snapshotFlow
import androidx.compose.ui.Modifier
import androidx.compose.ui.geometry.Offset
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.graphics.StrokeCap
import androidx.compose.ui.hapticfeedback.HapticFeedbackType
import androidx.compose.ui.input.pointer.pointerInput
import androidx.compose.ui.input.pointer.util.VelocityTracker
import androidx.compose.ui.platform.LocalDensity
import androidx.compose.ui.platform.LocalHapticFeedback
import androidx.compose.ui.text.TextStyle
import androidx.compose.ui.text.drawText
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.rememberTextMeasurer
import androidx.compose.ui.unit.Dp
import androidx.compose.ui.unit.TextUnit
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import kotlinx.coroutines.launch
import kotlin.math.abs
import kotlin.math.roundToInt

enum class RulerOrientation { Horizontal, Vertical }

@Stable
class RulerPickerState internal constructor(initialValue: Float) {
    internal val animatable = Animatable(initialValue)
    val currentValue: Float get() = animatable.value
    internal var lastHapticStep: Int = initialValue.roundToInt()

    suspend fun animateTo(value: Int) {
        animatable.animateTo(value.toFloat(), tween(300))
    }

    suspend fun snapTo(value: Int) {
        animatable.snapTo(value.toFloat())
    }
}

@Composable
fun rememberRulerPickerState(initialValue: Int, vararg keys: Any?): RulerPickerState =
    remember(*keys) { RulerPickerState(initialValue.toFloat()) }

@Composable
fun RulerPicker(
    state: RulerPickerState,
    min: Int,
    max: Int,
    majorEvery: Int,
    modifier: Modifier = Modifier,
    step: Int = 1,
    orientation: RulerOrientation = RulerOrientation.Horizontal,
    ticksFromEnd: Boolean = false,
    tickSpacing: Dp = 10.dp,
    minorTickLength: Dp = 25.dp,
    majorTickLength: Dp = 42.dp,
    tickWidth: Dp = 1.dp,
    indicatorWidth: Dp = 3.dp,
    indicatorLength: Dp = Dp.Unspecified,
    indicatorColor: Color = Color(0xFFDD224D),
    tickColor: Color = Color(0xFFCCCCCC),
    majorTickColor: Color = Color(0xFFAAAAAA),
    labelColor: Color = Color(0xFFAAAAAA),
    labelFontSize: TextUnit = 17.sp,
    labelFormatter: (Int) -> String = { it.toString() },
    onValueChanged: (Int) -> Unit = {},
) {
    val density = LocalDensity.current
    val haptic = LocalHapticFeedback.current
    val scope = rememberCoroutineScope()
    val textMeasurer = rememberTextMeasurer()

    val minF = min.toFloat()
    val maxF = max.toFloat()
    val tickSpacingPx = with(density) { tickSpacing.toPx() }

    LaunchedEffect(min, max) {
        state.animatable.updateBounds(minF, maxF)
        val clamped = state.animatable.value.coerceIn(minF, maxF)
        if (clamped != state.animatable.value) {
            state.animatable.snapTo(clamped)
        }
    }

    val currentOnValueChanged by rememberUpdatedState(onValueChanged)
    LaunchedEffect(state, step, min) {
        snapshotFlow {
            val raw = state.animatable.value
            val stepsFromMin = ((raw - minF) / step).roundToInt()
            (min + stepsFromMin * step).coerceIn(min, max)
        }.collect { intValue ->
            currentOnValueChanged(intValue)
            if (intValue != state.lastHapticStep) {
                state.lastHapticStep = intValue
                haptic.performHapticFeedback(HapticFeedbackType.VirtualKey)
            }
        }
    }

    Canvas(
        modifier = modifier.pointerInput(min, max, orientation, tickSpacingPx) {
            val velTracker = VelocityTracker()
            awaitEachGesture {
                val down = awaitFirstDown(requireUnconsumed = false)
                scope.launch { state.animatable.stop() }
                velTracker.resetTracking()
                velTracker.addPosition(down.uptimeMillis, down.position)

                drag(down.id) { change ->
                    velTracker.addPosition(change.uptimeMillis, change.position)
                    val delta = change.position - change.previousPosition
                    val primaryDrag = when (orientation) {
                        RulerOrientation.Horizontal -> delta.x
                        RulerOrientation.Vertical -> delta.y
                    }
                    val valueDelta = -primaryDrag / tickSpacingPx * step
                    scope.launch {
                        state.animatable.snapTo(
                            (state.animatable.value + valueDelta).coerceIn(minF, maxF),
                        )
                    }
                    change.consume()
                }

                val velocity = velTracker.calculateVelocity()
                val primaryVelocity = when (orientation) {
                    RulerOrientation.Horizontal -> velocity.x
                    RulerOrientation.Vertical -> velocity.y
                }
                val valueVelocity = -primaryVelocity / tickSpacingPx * step

                scope.launch {
                    if (abs(primaryVelocity) > 300f) {
                        state.animatable.animateDecay(
                            initialVelocity = valueVelocity,
                            animationSpec = exponentialDecay(frictionMultiplier = 1f),
                        )
                    }
                    val raw = state.animatable.value
                    val stepsFromMin = ((raw - minF) / step).roundToInt()
                    val snapped = (minF + stepsFromMin * step).coerceIn(minF, maxF)
                    state.animatable.animateTo(snapped, tween(150))
                }
            }
        },
    ) {
        val isHorizontal = orientation == RulerOrientation.Horizontal
        val primarySize = if (isHorizontal) size.width else size.height
        val secondarySize = if (isHorizontal) size.height else size.width
        val center = primarySize / 2f

        val tickAreaSize = if (indicatorLength == Dp.Unspecified) secondarySize else indicatorLength.toPx()

        val rawValue = state.animatable.value
        val halfRange = ((center / tickSpacingPx).toInt() + 1) * step
        val visibleMin = alignToStep(((rawValue - halfRange).toInt()).coerceAtLeast(min), min, step)
        val visibleMax = ((rawValue + halfRange).toInt()).coerceAtMost(max)

        val minorTickLenPx = minorTickLength.toPx()
        val majorTickLenPx = majorTickLength.toPx()
        val tickWidthPx = tickWidth.toPx()
        val indicatorWidthPx = indicatorWidth.toPx()
        val gap = 12.dp.toPx()

        val tickStart = if (ticksFromEnd) tickAreaSize else 0f

        var value = visibleMin
        while (value <= visibleMax) {
            val offset = center + (value - rawValue) / step * tickSpacingPx
            val isMajor = majorEvery > 0 && (value - min) % (majorEvery * step) == 0
            val tickLen = if (isMajor) majorTickLenPx else minorTickLenPx
            val color = if (isMajor) majorTickColor else tickColor

            val fade = (1f - abs(offset - center) / center).coerceAtLeast(0.3f)
            val fadedColor = color.copy(alpha = color.alpha * fade)

            val tickEnd = if (ticksFromEnd) tickAreaSize - tickLen else tickLen
            if (isHorizontal) {
                drawLine(fadedColor, Offset(offset, tickStart), Offset(offset, tickEnd), tickWidthPx, StrokeCap.Round)
            } else {
                drawLine(fadedColor, Offset(tickStart, offset), Offset(tickEnd, offset), tickWidthPx, StrokeCap.Round)
            }

            if (isMajor) {
                val label = labelFormatter(value)
                val measured = textMeasurer.measure(
                    text = label,
                    style = TextStyle(
                        color = labelColor.copy(alpha = labelColor.alpha * fade),
                        fontSize = labelFontSize,
                        fontWeight = FontWeight.Medium,
                    ),
                )
                if (isHorizontal) {
                    val labelY = if (ticksFromEnd) tickAreaSize - majorTickLenPx - gap - measured.size.height
                        else majorTickLenPx + gap
                    drawText(measured, topLeft = Offset(offset - measured.size.width / 2f, labelY))
                } else {
                    val labelX = if (ticksFromEnd) tickAreaSize - majorTickLenPx - gap - measured.size.width
                        else majorTickLenPx + gap
                    drawText(measured, topLeft = Offset(labelX, offset - measured.size.height / 2f))
                }
            }
            value += step
        }

        if (isHorizontal) {
            drawLine(indicatorColor, Offset(center, 0f), Offset(center, tickAreaSize), indicatorWidthPx, StrokeCap.Round)
        } else {
            drawLine(indicatorColor, Offset(0f, center), Offset(tickAreaSize, center), indicatorWidthPx, StrokeCap.Round)
        }
    }
}

private fun alignToStep(value: Int, min: Int, step: Int): Int {
    if (step <= 1) return value
    val offset = (value - min) % step
    return if (offset == 0) value else value - offset
}
